import React, { useMemo, useEffect, useRef, memo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Alert, Button } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import ChatMessage from './ChatMessage';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { useTts } from '../../context/TtsContext';
import { useResources } from '../../context/ResourceContext';
import { useModalBackHandler } from '../../context/ModalBackHandlerContext';
import { useConfig } from '../../context/ConfigContext';
import { useSession } from '../../context/SessionContext';
import LockIcon from '@mui/icons-material/Lock';
import { segmentTimeline } from '../../utils/timelineUtils';
import { getEffectRenderer } from './effects/EffectRegistry';
import { replaceResourceTags } from '../../utils/textProcessing';
import AILimitationsNotice from './AILimitationsNotice';

const displayPrefixFilters = [
    /^\[Context\].*?\[\/Context\]\s*/i,
    /^\[FILE\].*?\[\/FILE\]\s*/i,
    /^\[RESOURCE\].*?\[\/RESOURCE\]\s*/i,
    /^\[Message\]\s*(User|System):\s*/i,
];

const filterDisplayText = (text) => {
    if (typeof text !== 'string') {
        return text;
    }

    let processedText = text;

    const contextBlockRegex = /# Context from[\s\S]*?\n\`\`\`\n/g;
    processedText = processedText.replace(contextBlockRegex, '');

    const includedResourcesRegex = /--- Included Resources ---[\s\S]*/g;
    processedText = processedText.replace(includedResourcesRegex, '');

    for (const regex of displayPrefixFilters) {
        while (regex.test(processedText)) {
            processedText = processedText.replace(regex, '');
        }
    }

    return processedText.replace(/(\[\/WAIT\]|\[WAIT\/\])/gi, '').trim();
};

/**
 * Returns the localStorage key used to persist the dismissed notice ID.
 */
const getNoticeDismissKey = (prefix) =>
    `${prefix || 'app'}_ai_limitations_notice_dismissed`;

const ChatMessageList = ({
    timelineItems = [],
    showProcessUpdates = true
}) => {
    const { isSubscriptionRequired } = useAuth();
    const { openSubscriptionDialog } = useUI();
    const { isAutoPlayEnabled, startTts, isPlaying, isPreparing, currentlyPlayingId } = useTts();
    const { getResourceById } = useResources();
    const { modalCount } = useModalBackHandler();
    const { agentEffectFilters, aiLimitationsNotice, chatTeamConfigs, localStoragePrefix } = useConfig();
    const { teamName } = useSession();

    const lastPlayedMessageIdRef = useRef(null);
    const virtuosoRef = useRef(null);
    const prevDataLengthRef = useRef(0);
    const [atBottom, setAtBottom] = useState(true);

    // --- AI Limitations Notice dismiss state ---
    // Stored in localStorage keyed by noticeId so that updating the ID in config
    // forces the notice to reappear for all users, even those who previously dismissed it.
    const noticeStorageKey = getNoticeDismissKey(localStoragePrefix);
    const currentNoticeId = aiLimitationsNotice?.noticeId;

    const [noticeDismissed, setNoticeDismissed] = useState(() => {
        if (!currentNoticeId) return false;
        try {
            return localStorage.getItem(noticeStorageKey) === currentNoticeId;
        } catch {
            return false;
        }
    });

    const handleDismissNotice = useCallback(() => {
        if (!currentNoticeId) return;
        try {
            localStorage.setItem(noticeStorageKey, currentNoticeId);
        } catch {
            // localStorage unavailable — dismiss for this session only
        }
        setNoticeDismissed(true);
    }, [noticeStorageKey, currentNoticeId]);

    const activeModalCount = typeof modalCount === 'number' ? modalCount : 0;

    const lastItem = timelineItems.length > 0 ? timelineItems[timelineItems.length - 1] : null;
    const isAgentFinished = lastItem && lastItem.type === 'message' && lastItem.source !== 'User' && !lastItem.isTemporary;

    useEffect(() => {
        if (!isAutoPlayEnabled || timelineItems.length === 0 || isPlaying || isPreparing) {
            return;
        }

        const lastTutorMessage = [...timelineItems].reverse().find(item =>
            item.type === 'message' && item.source !== 'User' && typeof item.content === 'string'
        );

        if (
            lastTutorMessage &&
            lastTutorMessage.id !== lastPlayedMessageIdRef.current
        ) {
            const rawText = filterDisplayText(lastTutorMessage.content);
            const displayText = replaceResourceTags(rawText, getResourceById);

            if (displayText) {
                console.log(`[AutoPlay TTS] Triggering for message ID: ${lastTutorMessage.id}`);
                const chunks = displayText.split(/\n\s*\n/).filter(p => p.trim().length > 0);
                const playlist = chunks.map((chunk, index) => ({
                    id: `${lastTutorMessage.id}-${index}`,
                    text: chunk,
                }));

                if (playlist.length > 0) {
                    startTts(playlist, `chat-message-${lastTutorMessage.id}`);
                    lastPlayedMessageIdRef.current = lastTutorMessage.id;
                }
            }
        }
    }, [timelineItems, isAutoPlayEnabled, startTts, isPlaying, isPreparing, getResourceById]);

    const displayItems = useMemo(() => {
        if (!showProcessUpdates) {
            return timelineItems
                .filter(item => item.type === 'message')
                .map((item, index) => ({ type: 'message', data: item, key: item.id || `msg-idx-${index}` }));
        }
        return segmentTimeline(timelineItems, agentEffectFilters);
    }, [timelineItems, showProcessUpdates, agentEffectFilters]);

    // Determine once whether the notice should be shown for the current team
    const showAILimitationsNotice = useMemo(() => {
        if (!aiLimitationsNotice) return false;
        if (noticeDismissed) return false;
        const teamConfig = chatTeamConfigs?.[teamName];
        return !teamConfig?.disableAILimitationsNotice;
    }, [aiLimitationsNotice, chatTeamConfigs, teamName, noticeDismissed]);

    const finalDisplayItems = useMemo(() => {
        if (!displayItems) return [];

        const grouped = [];
        let currentProcessGroup = [];
        let lastAgentMessageRef = null;

        const flushProcessGroup = () => {
            if (currentProcessGroup.length > 0) {
                if (lastAgentMessageRef) {
                    if (!lastAgentMessageRef.processDataForMessage) {
                        lastAgentMessageRef.processDataForMessage = [];
                    }
                    lastAgentMessageRef.processDataForMessage.push(...currentProcessGroup);
                } else {
                    const processGroupItem = {
                        type: 'process_group',
                        data: [...currentProcessGroup],
                        key: `proc-group-${currentProcessGroup[0].key}`
                    };
                    grouped.push(processGroupItem);
                }
                currentProcessGroup = [];
            }
        };

        displayItems.forEach((item) => {
            if (item.type === 'process') {
                currentProcessGroup.push(item);
            }
            else if (item.type === 'effect') {
                const rendererKey = item.data.resource_type || item.data.effect_key;
                const renderer = getEffectRenderer(rendererKey);

                if (!renderer) {
                    return;
                }

                if (renderer.shouldRender && !renderer.shouldRender(item.data)) {
                    return;
                }

                if (lastAgentMessageRef) {
                    flushProcessGroup();
                    if (!lastAgentMessageRef.effects) {
                        lastAgentMessageRef.effects = [];
                    }
                    lastAgentMessageRef.effects.push(item);
                } else {
                    const placeholder = {
                        type: 'message',
                        data: {
                            id: item.key,
                            content: '...',
                            source: 'System',
                            isTemporary: true
                        },
                        effects: [item],
                        key: item.key,
                        isPlaceholder: true,
                        processDataForMessage: [...currentProcessGroup]
                    };
                    grouped.push(placeholder);
                    lastAgentMessageRef = placeholder;
                    currentProcessGroup = [];
                }
            }
            else if (item.type === 'message') {
                const isAgent = item.data.source !== 'User' && item.data.source !== 'APP_EVENT';
                let attachedProcessData = null;
                let attachedProcessKey = null;

                if (isAgent && currentProcessGroup.length > 0) {
                    attachedProcessData = [...currentProcessGroup];
                    attachedProcessKey = `proc-group-${currentProcessGroup[0].key}`;
                    currentProcessGroup = [];
                } else {
                    flushProcessGroup();
                }

                if (isAgent && lastAgentMessageRef && lastAgentMessageRef.isPlaceholder) {
                    const placeholder = grouped.pop();
                    const mergedEffects = [...(placeholder.effects || []), ...(item.effects || [])];
                    let mergedProcessData = attachedProcessData || [];
                    if (placeholder.processDataForMessage) {
                        mergedProcessData = [...placeholder.processDataForMessage, ...mergedProcessData];
                    }

                    const messageItem = {
                        ...item,
                        data: { ...item.data },
                        processDataForMessage: mergedProcessData.length > 0 ? mergedProcessData : null,
                        processGroupKey: attachedProcessKey || placeholder.processGroupKey,
                        effects: mergedEffects
                    };
                    grouped.push(messageItem);
                    lastAgentMessageRef = messageItem;
                } else {
                    const messageItem = {
                        ...item,
                        data: { ...item.data },
                        processDataForMessage: attachedProcessData,
                        processGroupKey: attachedProcessKey,
                        effects: []
                    };
                    grouped.push(messageItem);

                    if (isAgent) {
                        lastAgentMessageRef = messageItem;
                    } else {
                        lastAgentMessageRef = null;
                    }
                }
            }
        });

        flushProcessGroup();

        // INJECT AI LIMITATIONS NOTICE
        // Only inject if the notice exists, is not disabled for the current team,
        // and has not been dismissed by the user for the current noticeId.
        // When the component returns null (disabled/dismissed), Virtuoso still
        // allocates a list slot and renders an empty wrapper, triggering a
        // "Zero-sized element" error. Guarding here prevents that entirely.
        if (showAILimitationsNotice) {
            const welcomeIndex = grouped.findIndex(item =>
                item.key === 'welcome-options' || item.data?.id === 'welcome-options'
            );

            const noticeItem = {
                type: 'custom_component',
                key: 'ai-limitations-notice',
                component: <AILimitationsNotice onDismiss={handleDismissNotice} />
            };

            if (welcomeIndex !== -1) {
                grouped.splice(welcomeIndex + 1, 0, noticeItem);
            } else {
                grouped.unshift(noticeItem);
            }
        }

        return grouped;
    }, [displayItems, showAILimitationsNotice, handleDismissNotice]);

    useEffect(() => {
        if (!virtuosoRef.current) return;

        const currentLength = finalDisplayItems.length;
        const prevLength = prevDataLengthRef.current;

        if (currentLength > prevLength) {
            if (activeModalCount === 0) {
                setTimeout(() => {
                    virtuosoRef.current?.scrollToIndex({
                        index: currentLength - 1,
                        align: 'end',
                        behavior: 'smooth'
                    });
                }, 50);
                prevDataLengthRef.current = currentLength;
            }
        }
        else if (prevLength === 0 && currentLength > 0) {
            if (activeModalCount === 0) {
                setTimeout(() => {
                    virtuosoRef.current?.scrollToIndex({
                        index: currentLength - 1,
                        align: 'end',
                        behavior: 'auto'
                    });
                }, 50);
                prevDataLengthRef.current = currentLength;
            }
        }
        else if (currentLength <= prevLength) {
            prevDataLengthRef.current = currentLength;
        }
    }, [finalDisplayItems, activeModalCount]);

    const lastAnimatedIndex = useMemo(() => {
        if (isAgentFinished) {
            return -1;
        }

        let candidateIndex = -1;
        for (let i = finalDisplayItems.length - 1; i >= 0; i--) {
            const item = finalDisplayItems[i];
            if (item.type === 'custom_component') continue;

            if (item.type === 'process_group') {
                candidateIndex = i;
                break;
            }
            if (item.type === 'message' && item.data.isTemporary && item.data.content === '...') {
                candidateIndex = i;
                break;
            }
        }

        if (candidateIndex !== -1 && candidateIndex === finalDisplayItems.length - 1) {
            return candidateIndex;
        }
        return -1;
    }, [finalDisplayItems, isAgentFinished]);

    const lastAgentMessageId = useMemo(() => {
        if (!finalDisplayItems || finalDisplayItems.length === 0) return null;

        for (let i = finalDisplayItems.length - 1; i >= 0; i--) {
            const item = finalDisplayItems[i];
            if (item.type === 'custom_component') continue;

            if (item.type === 'process_group') {
                return item.key;
            }
            if (
                item.type === 'message' &&
                item.data.source !== 'User' &&
                item.data.source !== 'APP_EVENT'
            ) {
                return item.data.id;
            }
        }
        return null;
    }, [finalDisplayItems]);

    useEffect(() => {
        if (timelineItems.length > 0) {
            const lastItem = timelineItems[timelineItems.length - 1];
            if (lastItem.scrollMode === 'top' && virtuosoRef.current) {
                const index = finalDisplayItems.findIndex(item =>
                    item.type === 'message' && item.data.id === lastItem.id
                );
                if (index !== -1) {
                    setTimeout(() => {
                        virtuosoRef.current.scrollToIndex({ index, align: 'start', behavior: 'smooth' });
                    }, 100);
                }
            }
        }
    }, [timelineItems, finalDisplayItems]);

    const renderItem = useCallback((index, item) => {
        if (item.type === 'custom_component') {
            return <Box key={item.key} sx={{ mb: 3 }}>{item.component}</Box>;
        }

        const isAnimated = index === lastAnimatedIndex;
        const itemSx = {
            px: 2,
            '& .MuiPaper-root': {
                minWidth: '180px'
            }
        };

        if (item.type === 'process_group') {
            const processData = { processTree: item.data.flatMap(p => p.data.processTree) };
            const placeholderMessage = {
                id: item.key,
                text: '...',
                originalText: '...',
                sender: 'system',
                timestamp: new Date().toISOString(),
                isTemporary: true,
                isAnimated: isAnimated,
            };

            const isLastAgentMessage = item.key === lastAgentMessageId;

            return (
                <Box sx={itemSx}>
                    <ChatMessage
                        key={item.key}
                        message={placeholderMessage}
                        processData={processData}
                        currentlyPlayingId={currentlyPlayingId}
                        isLastAgentMessage={isLastAgentMessage}
                    />
                </Box>
            );
        }

        if (item.type === 'message') {
            const processData = item.processDataForMessage ? { processTree: item.processDataForMessage.flatMap(p => p.data.processTree) } : null;

            const isContentElement = React.isValidElement(item.data.content);
            const messageText = isContentElement
                ? item.data.content
                : filterDisplayText(item.data.content);

            if (!isContentElement && !messageText && !processData && (!item.effects || item.effects.length === 0)) {
                return null;
            }

            const messageData = {
                ...item.data,
                text: messageText,
                originalText: typeof item.data.content === 'string' ? item.data.content : '',
                sender: item.data.source === 'User' ? 'user' : (item.data.source === 'APP_EVENT' ? 'app' : 'system'),
                isAnimated: isAnimated,
            };

            const isLastAgentMessage = messageData.id === lastAgentMessageId;

            return (
                <Box sx={itemSx}>
                    <ChatMessage
                        key={item.processGroupKey || item.key}
                        message={messageData}
                        processData={processData}
                        effects={item.effects}
                        currentlyPlayingId={currentlyPlayingId}
                        isLastAgentMessage={isLastAgentMessage}
                    />
                </Box>
            );
        }

        return null;
    }, [lastAnimatedIndex, lastAgentMessageId, currentlyPlayingId]);

    const Footer = useCallback(() => {
        if (!isSubscriptionRequired) return <Box sx={{ height: 32 }} />;

        return (
            <Alert
                severity="error"
                icon={<LockIcon />}
                sx={{
                    m: 2,
                    alignItems: 'center',
                }}
                action={
                    <Button
                        color="inherit"
                        size="small"
                        variant="outlined"
                        onClick={openSubscriptionDialog}
                    >
                        View Plans
                    </Button>
                }
            >
                <Typography fontWeight="bold">Your trial has ended.</Typography>
                <Typography variant="body2">
                    Please select a subscription plan to continue.
                </Typography>
            </Alert>
        );
    }, [isSubscriptionRequired, openSubscriptionDialog]);

    const Header = useCallback(() => {
        return <Box sx={{ height: 16 }} />;
    }, []);

    if (finalDisplayItems.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                    No activity yet. Start the conversation!
                </Typography>
            </Box>
        );
    }

    return (
        <Virtuoso
            ref={virtuosoRef}
            style={{ height: '100%', width: '100%' }}
            data={finalDisplayItems}
            itemContent={renderItem}
            components={{ Header, Footer }}
            followOutput={activeModalCount > 0 ? false : "smooth"}
            atBottomStateChange={setAtBottom}
            initialTopMostItemIndex={Math.max(0, finalDisplayItems.length - 1)}
        />
    );
};

ChatMessageList.propTypes = {
    timelineItems: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string.isRequired,
        timestamp: PropTypes.string.isRequired,
        content: PropTypes.any,
        source: PropTypes.string,
        to: PropTypes.string,
        process_ids: PropTypes.arrayOf(PropTypes.shape({
            type: PropTypes.string,
            id: PropTypes.string,
        })),
    })),
    showProcessUpdates: PropTypes.bool
};

export default memo(ChatMessageList);